import { Component } from "react";
import "./App.css";
import ListCourses from "./ListCourses.js";
import Search from "./Search.js";
import * as CoursesApi from "./CoursesApi.js";
import { Route, Switch } from "react-router";
import { v4 as uuidv4 } from 'uuid';
import debounce from "lodash.debounce";

class CourseApp extends Component {
  state = {
    courses: [], // Courses user has assigned a status to; stored in db
    retrieved: [], // Courses retrieved through requests to api by user's searches
    query: "", // Search query entered by user
    // course number-color pairs to document which course has what color;
    // NOTE that this gets updated each time new courses are searched up and need to be assigned new random colors
    numbers: {},
    pagination: {}, // Pagination info from requests to api
    links: {}, // Links to navigate through pages of searched up courses from requests to api
  };

  // Update (all cases except removing) course status according to change in selector values in both frontend and backend 
  updateCourse = (id, updatedStatus) => {
    CoursesApi.patch(id, updatedStatus)
      .then(course => {
        this.setState(state => {
          let index = state.courses.findIndex(c => c._id === course._id);
          const localCourse = state.courses[index];
          localCourse.status = course.status;
          return {
            courses: state.courses,
            retrieved: state.retrieved.map(c => {
              if (c.number === course.number && c.term === course.term) c.status = course.status;
              return c;
            }),
          };
        });
    });
  }

  // Remove course frmo both frontend and backend upon selecting "none" status for course via selector
  removeCourse = (id) => {
    CoursesApi.remove(id)
      .then(course => {
        this.setState(state => {
          state.courses = state.courses.filter(c => c._id !== course._id);
          return {
            courses: state.courses,
            retrieved: state.retrieved.map(c => {
              if (c.number === course.number && c.term === course.term) c.status = "none";
              return c;
            }),
          };
        });
    });
  }

  // Update status of retrieved course from the search page (/search), consequently updating same course
  // on the main page (/) in both frontend and backend
  updateRetrieved = (id, updatedStatus) => {
    let toAdd;
    this.setState(state => {
      let index = state.retrieved.findIndex(c => c._id === id);
      const localCourse = state.retrieved[index];
      localCourse.status = updatedStatus;
      if (state.courses.find(c => c.number === localCourse.number && c.term === localCourse.term)) {
        state.courses.map(c => {
          if (c.number === localCourse.number && c.term === localCourse.term) {
            CoursesApi.patch(c._id, updatedStatus)
              .then(course => {
                c.status = course.status;
              });
          }
          return c;
        });
      } else {
        // Course info to be stored in db
        toAdd = {
          status: updatedStatus,
          title: localCourse.title,
          number: localCourse.number,
          term: localCourse.term,
        };
        CoursesApi.add(toAdd)
          .then(course => {
            this.setState(state => {
              const localAdd = course;
              localAdd._id = course._id;
              localAdd.color = this.state.numbers[course.number];
              return {
                courses: state.courses.concat(localAdd),
              }
            });
          });
      }
      return { retrieved: state.retrieved, courses: state.courses };
    });
  }

  // Remove course from both frontend and backend upon selecting "none" status on search page (/search)
  removeRetrieved = (id) => {
    const localCourse = this.state.courses.find(c => {
      const retrievedCourse = this.state.retrieved.find(rc => rc._id === id);
      return retrievedCourse
        ? c.number === retrievedCourse.number && c.term === retrievedCourse.term
        : false;
    });
    if (localCourse) {
      CoursesApi.remove(localCourse._id)
        .then(course => {
          this.setState(state => {
            state.courses = state.courses.filter(c => c._id !== course._id);
            return {
              courses: state.courses,
              retrieved: state.retrieved.map(c => {
                if (c.number === course.number && c.term === course.term) c.status = "none";
                return c;
              }),
            };
          });
        });
    }
  }

  // Assign random colors to course cards such that courses with same number has same color throughout
  // operations on both the main page and the search page and searching, until page refresh, after which
  // colors for courses will be randomized again but the aforementioned persistence maintains 
  assignRandColor = (list, id) => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    if (list === "courses") {
      const course = this.state.courses.find(c => c._id === id);
      if (course && !this.state.numbers[course.number]) {
        for (let i = 0; i < 6; i++) {
          color += letters[Math.floor(Math.random() * 16)];
        }
        this.setState(state => {
          state.numbers[course.number] = color;
          return { numbers: state.numbers };
        }, () => {
          this.setState(state => {
            return {
              courses: state.courses.map(c => {
                if (c._id === id) c.color = this.state.numbers[course.number];
                return c;
              }),
            };
          });
        });
      } else if (course) {
        this.setState(state => {
          return {
            courses: state.courses.map(c => {
              if (c._id === id) c.color = this.state.numbers[course.number];
              return c;
            }),
          };
        });
      }
    } else if (list === "retrieved") {
      const course = this.state.retrieved.find(c => c._id === id);
      if (course && !this.state.numbers[course.number]) {
        for (let i = 0; i < 6; i++) {
          color += letters[Math.floor(Math.random() * 16)];
        }
        this.setState(state => {
          state.numbers[course.number] = color;
          return { numbers: state.numbers };
        }, () => {
          this.setState(state => {
            return {
              retrieved: state.retrieved.map(c => {
                if (c._id === id) c.color = this.state.numbers[course.number];
                return c;
              }),
            };
          });
        });
      } else if (course) {
        this.setState(state => {
          return {
            retrieved: state.retrieved.map(c => {
              if (c._id === id) c.color = this.state.numbers[course.number];
              return c;
            }),
          };
        });
      }
    }
  }

  componentDidMount() {
    // Fetch all courses stored in db and assign random color according to the behavior specified
    // in the homework prompt
    CoursesApi.getAll().then(courses => this.setState({ courses }, () => {
      this.state.courses.map(course => this.assignRandColor("courses", course._id));
    }));
  }

  // Update the query in state from changes of user input on the search page (/search)
  updateQuery = (query) => {
    this.setState({ query });
  };

  // Debounced function that delays sending requests to api upon user input; send requests to api
  // when user has stopped typing for 500 milliseconds
  delayedRetrieval = debounce((query) => {
    if (!query) this.setState({ retrieved: [] });
    else {
      CoursesApi.retrieve(query)
        .then(retrieved => {
          this.updateRetrievedStatus(retrieved.data);
          this.setState({ pagination: retrieved.pagination });
          this.setState({ links: retrieved.links });
          // If query reported from api does not match the most recent query in state, then
          // call delayedRetrieval again with the latest query to sync
          if (retrieved.query !== this.state.query.trim()) this.delayedRetrieval(this.state.query);
        });
    }
  }, 500);

  // Helper function to populate the retrieved array in state and giving local retrieved course objects
  // statuses that match those of the same courses on the main page (/)
  updateRetrievedStatus = (retrieved) => {
    this.setState({
      retrieved:
        retrieved.map(course => {
          const match = this.state.courses.find(c => c.number === course.number && c.term === course.term);
          course.status = match ? match.status : "none";
          course._id = uuidv4();
          return course;
        }),
    }, () => {
      this.state.retrieved.map(course => this.assignRandColor("retrieved", course._id));
    });
  }

  // Navigate to certain page in courses searched from query to update search page (/search)
  navRetrieved = (url) => {
    CoursesApi.nav(url)
      .then(retrieved => {
        this.updateRetrievedStatus(retrieved.data)
        if (retrieved.data) {
          this.setState({ pagination: retrieved.pagination });
          this.setState({ links: retrieved.links });
        }
      });
  }

  render() {
    return (
      <div className="app">
        <Switch>
          <Route exact path="/">
            <ListCourses
              courses={this.state.courses}
              updateCourse={this.updateCourse}
              removeCourse={this.removeCourse}
            />
          </Route>
          <Route path="/search">
            <Search
              query={this.state.query}
              updateQuery={this.updateQuery}
              delayedRetrieval={this.delayedRetrieval}
              retrieved={this.state.retrieved}
              updateCourse={this.updateRetrieved}
              removeCourse={this.removeRetrieved}
              pagination={this.state.pagination}
              links={this.state.links}
              navRetrieved={this.navRetrieved}
            />
          </Route>
        </Switch>
      </div>
    );
  }
}

export default CourseApp;
